import React, { ErrorInfo, ReactNode } from 'react';

import { Alert, Box, Button, Modal } from '@amzn/awsui-components-react-v3';

import urls from '../../../resources/urls';

interface ErrorBoundaryProps {
  setGraphQLError: (err: string) => void;
  apolloError: string;
  isExternalUser: boolean;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  apolloError: string;
  isErrorVisible: boolean;
}

const EXTERNAL_USER_DEFAULT_ERROR_MESSAGE =
  "It appears something went wrong while loading the app. " +
  "Please request your account manager to contact AWS if this problem persists.";
const EXTERNAL_USER_PERMISSION_ERROR_MESSAGE =
  "You do not have adequate permissions to view this page. " +
  "Please contact your account manager to request the right permissions";

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      error: null,
      errorInfo: null,
      apolloError: props.apolloError,
      isErrorVisible: false
    };
  }

  /**
   * To force update apolloError from input props
   */
  UNSAFE_componentWillReceiveProps(props: ErrorBoundaryProps) {
    this.setState((state: ErrorBoundaryState) => {
      state.isErrorVisible = props.apolloError !== '';
      state.apolloError = props.apolloError;
      return state;
    });
  }

  /**
   * To update React errors
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      isErrorVisible: true,
      error,
      errorInfo: errorInfo
    });
  }

  render() {
    const isValidApolloError = this.state.apolloError && this.state.apolloError !== '';
    
    // Always return consistent JSX structure to prevent wizard step reset
    // when error modal appears/disappears
    return (
      <div>
        {(this.state.errorInfo || isValidApolloError) && 
          (this.props.isExternalUser ? this.buildExternalUserErrorModal() : this.buildErrorModal())
        }
        {this.props.children}
      </div>
    );
  }

  buildErrorModal = () => {
    return (
      <Modal
        data-test-id="internal-user-error-modal"
        visible={this.state.isErrorVisible}
        header="Error"
        footer={
          <Box float="right">
            <Button iconName="bug" href={urls.SIM_TICKET_URL} target="_blank">
              Report A Bug
            </Button>
          </Box>
        }
        onDismiss={() => {
          this.setState({
            isErrorVisible: false
          });
          this.props.setGraphQLError('');
        }}
      >
        <Alert type="error">
          {this.state.error?.message ?? this.state.apolloError}
        </Alert>
        <Box margin={{ top: 'l' }}>
          Please refresh the page or report the bug to SMART team.
        </Box>
      </Modal>
    );
  }

  buildExternalUserErrorModal = () => {
    return (
      <Modal
        data-test-id="external-user-error-modal"
        visible={this.state.isErrorVisible}
        header="Error"
        onDismiss={() => {
          this.setState({
            isErrorVisible: false
          });
          this.props.setGraphQLError('');
        }}
      >
        <Alert type="error">
          {this.state.error?.message ?? this.sanitizeErrorMessage(this.state.apolloError)}
        </Alert>
      </Modal>
    );
  }

  sanitizeErrorMessage = (message: string) => {
    if (message.includes('is not authorized to perform: '))
      return EXTERNAL_USER_PERMISSION_ERROR_MESSAGE;
    return EXTERNAL_USER_DEFAULT_ERROR_MESSAGE;
  }
}

export default ErrorBoundary;
